import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Appointment {
	id: number;
	customer_id: number;
	service_id: number;
	staff_id: number;
	first_name: string;
	last_name: string;
	full_name: string;
	phone: string;
	email: string;
	appointment_date: string;
	start_time: string;
	end_time: string;
	formatted_time: string;
	duration_minutes: number;
	status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
	notes: string;
	remarks: string;
	total_amount: number;
	is_consultation: boolean;
	consultation_mode?: 'online' | 'offline';
	zoom_meeting_id?: string;
	zoom_join_url?: string;
	customer?: any;
	service?: any;
	staff?: any;
	created_at: string;
}

export interface TimeSlot {
	start: string;
	end: string;
	display: string;
}

export interface ApiResponse<T> {
	status: boolean;
	data: T;
	message?: string;
}

export interface PaginatedResponse<T> {
	current_page: number;
	data: T[];
	last_page: number;
	per_page: number;
	total: number;
}

@Injectable({
	providedIn: 'root',
})
export class AppointmentService {
	private http = inject(HttpClient);
	private apiUrl = environment.apiUrl;

	// Admin APIs
	getAll(filters?: { date?: string; start_date?: string; end_date?: string; status?: string; staff_id?: number; page?: number }): Observable<ApiResponse<PaginatedResponse<Appointment>>> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					params = params.set(key, value.toString());
				}
			});
		}
		return this.http.get<ApiResponse<PaginatedResponse<Appointment>>>(`${this.apiUrl}/admin/appointments`, { params });
	}

	getByDate(date: string): Observable<ApiResponse<Appointment[]>> {
		return this.http.get<ApiResponse<Appointment[]>>(`${this.apiUrl}/admin/appointments/date/${date}`);
	}

	getStats(): Observable<ApiResponse<any>> {
		return this.http.get<ApiResponse<any>>(`${this.apiUrl}/admin/appointments/stats`);
	}

	getCalendarData(startDate: string, endDate: string): Observable<ApiResponse<any[]>> {
		let params = new HttpParams()
			.set('start_date', startDate)
			.set('end_date', endDate);
		return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/admin/appointments/calendar`, { params });
	}

	create(data: Partial<Appointment>): Observable<ApiResponse<Appointment>> {
		return this.http.post<ApiResponse<Appointment>>(`${this.apiUrl}/admin/appointments`, data);
	}

	update(id: number, data: Partial<Appointment>): Observable<ApiResponse<Appointment>> {
		return this.http.put<ApiResponse<Appointment>>(`${this.apiUrl}/admin/appointments/${id}`, data);
	}

	updateStatus(id: number, status: string, remarks?: string): Observable<ApiResponse<Appointment>> {
		return this.http.patch<ApiResponse<Appointment>>(`${this.apiUrl}/admin/appointments/${id}/status`, { status, remarks });
	}

	delete(id: number): Observable<ApiResponse<null>> {
		return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/admin/appointments/${id}`);
	}

	// Public API
	getAvailableSlots(date: string, staffId: number, serviceId: number, duration?: number): Observable<ApiResponse<{ slots: TimeSlot[] }>> {
		let params = new HttpParams()
			.set('date', date)
			.set('staff_id', staffId.toString())
			.set('service_id', serviceId.toString());
		if (duration) params = params.set('duration', duration.toString());
		return this.http.get<ApiResponse<{ slots: TimeSlot[] }>>(`${this.apiUrl}/public/appointments/available-slots`, { params });
	}
}
